// src/contexts/NavigationContext.js
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import indoorMappingService from '../services/indoorMappingService';

const NavigationContext = createContext({});

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  // Navigation mode: 'guided' (step-by-step) or 'dynamic' (GPS-guided)
  const [mode, setMode] = useState('guided');

  // Can user use dynamic mode? (requires GPS)
  const [canUseDynamic, setCanUseDynamic] = useState(false);

  // GPS status: 'disabled' | 'acquiring' | 'good' | 'weak' | 'lost'
  const [gpsStatus, setGpsStatus] = useState('disabled');

  // Current route
  const [route, setRoute] = useState(null);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // Current step index in guided mode
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Is navigation complete?
  const [isComplete, setIsComplete] = useState(false);

  // User's current position (from GPS)
  const [userPosition, setUserPosition] = useState(null);

  // Watch ID for geolocation
  const [watchId, setWatchId] = useState(null);

  // Accessibility options
  const [accessibleOnly, setAccessibleOnly] = useState(false);
  const [preferElevators, setPreferElevators] = useState(false);

  /**
   * Start navigation to a POI
   * @param {string} destinationPoiId - POI ID to navigate to
   * @param {string} entranceId - Building entrance ID (required by backend)
   * @param {Object} options - Additional options
   */
  const startNavigation = useCallback(async (destinationPoiId, entranceId, options = {}) => {
    try {
      if (!entranceId) {
        throw new Error('Entrance ID is required for navigation');
      }

      setIsLoading(true);
      setError(null);
      setIsComplete(false);
      setCurrentStepIndex(0);

      // Calculate route using backend A* pathfinding
      const routeData = await indoorMappingService.calculateRoute(
        entranceId,
        destinationPoiId,
        {
          accessible_only: options.accessible_only ?? accessibleOnly,
          prefer_elevators: options.prefer_elevators ?? preferElevators,
          use_cache: options.use_cache ?? true,
        }
      );

      setRoute(routeData);
      setIsLoading(false);
      return routeData;
    } catch (err) {
      const errorMessage = err.message || 'Failed to calculate route';
      setError(errorMessage);
      setIsLoading(false);
      throw err;
    }
  }, [accessibleOnly, preferElevators]);

  /**
   * Start navigation to a custom coordinate (not a POI)
   * @param {string} floorPlanId - Target floor plan ID
   * @param {number} x - X coordinate on floor plan
   * @param {number} y - Y coordinate on floor plan
   * @param {string} entranceId - Building entrance ID (required by backend)
   * @param {Object} options - Additional options
   */
  const startNavigationToCustom = useCallback(async (floorPlanId, x, y, entranceId, options = {}) => {
    try {
      if (!entranceId) {
        throw new Error('Entrance ID is required for navigation');
      }

      setIsLoading(true);
      setError(null);
      setIsComplete(false);
      setCurrentStepIndex(0);

      // Calculate route to custom coordinate using backend A* pathfinding
      const routeData = await indoorMappingService.calculateRouteToCustom(
        entranceId,
        floorPlanId,
        x,
        y,
        {
          accessible_only: options.accessible_only ?? accessibleOnly,
          prefer_elevators: options.prefer_elevators ?? preferElevators,
          use_cache: options.use_cache ?? true,
        }
      );

      setRoute(routeData);
      setIsLoading(false);
      return routeData;
    } catch (err) {
      const errorMessage = err.message || 'Failed to calculate route';
      setError(errorMessage);
      setIsLoading(false);
      throw err;
    }
  }, [accessibleOnly, preferElevators]);

  /**
   * Start navigation to a specific wayfinding node
   * @param {string} entranceId - Building entrance ID (required by backend)
   * @param {string} destinationNodeId - Target wayfinding node ID
   * @param {Object} options - Additional options
   * @param {Object} options.destinationInfo - Optional destination info (name, etc.) for display when not navigating to a POI
   */
  const startNavigationToNode = useCallback(async (entranceId, destinationNodeId, options = {}) => {
    try {
      if (!entranceId) {
        throw new Error('Entrance ID is required for navigation');
      }
      if (!destinationNodeId) {
        throw new Error('Destination node ID is required for navigation');
      }

      setIsLoading(true);
      setError(null);
      setIsComplete(false);
      setCurrentStepIndex(0);

      // Calculate route to specific wayfinding node using backend A* pathfinding
      const routeData = await indoorMappingService.calculateRouteToNode(
        entranceId,
        destinationNodeId,
        {
          accessible_only: options.accessible_only ?? accessibleOnly,
          prefer_elevators: options.prefer_elevators ?? preferElevators,
          use_cache: options.use_cache ?? true,
        }
      );

      // If backend didn't return destination and destinationInfo is provided (e.g., for alarm navigation),
      // use it as a fallback. This allows displaying destination name even when navigating to a node (not a POI)
      if (!routeData.destination && options.destinationInfo) {
        routeData.destination = options.destinationInfo;
      }

      setRoute(routeData);
      setIsLoading(false);
      return routeData;
    } catch (err) {
      const errorMessage = err.message || 'Failed to calculate route';
      setError(errorMessage);
      setIsLoading(false);
      throw err;
    }
  }, [accessibleOnly, preferElevators]);

  /**
   * Cancel navigation
   */
  const cancelNavigation = useCallback(() => {
    setRoute(null);
    setCurrentStepIndex(0);
    setIsComplete(false);
    setError(null);

    // Stop GPS tracking if enabled
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
  }, [watchId]);

  /**
   * Move to next step
   */
  const nextStep = useCallback(() => {
    if (!route || !route.steps) return;

    if (currentStepIndex < route.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      setIsComplete(true);
    }
  }, [route, currentStepIndex]);

  /**
   * Move to previous step
   */
  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setIsComplete(false);
    }
  }, [currentStepIndex]);

  /**
   * Go to specific step
   */
  const goToStep = useCallback((index) => {
    if (!route || !route.steps) return;

    if (index >= 0 && index < route.steps.length) {
      setCurrentStepIndex(index);
      setIsComplete(false);
    }
  }, [route]);

  /**
   * Enable dynamic (GPS-guided) mode
   */
  const enableDynamicMode = useCallback(() => {
    if (!canUseDynamic) {
      setError('GPS is not available or not accurate enough');
      return;
    }

    setMode('dynamic');
    setCurrentStepIndex(0);
  }, [canUseDynamic]);

  /**
   * Disable dynamic mode (revert to guided)
   */
  const disableDynamicMode = useCallback(() => {
    setMode('guided');
  }, []);

  /**
   * Update user position
   */
  const updateUserPosition = useCallback((position) => {
    setUserPosition({
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
    });

    // Update GPS status based on accuracy
    if (position.coords.accuracy <= 5) {
      setGpsStatus('good');
      setCanUseDynamic(true);
    } else if (position.coords.accuracy <= 15) {
      setGpsStatus('weak');
      setCanUseDynamic(true);
    } else {
      setGpsStatus('weak');
      setCanUseDynamic(false);
    }
  }, []);

  /**
   * Handle GPS error
   */
  const handleGpsError = useCallback((error) => {
    console.error('GPS error:', error);
    setGpsStatus('lost');
    setCanUseDynamic(false);

    if (mode === 'dynamic') {
      setMode('guided');
      setError('GPS signal lost. Switched to guided mode.');
    }
  }, [mode]);

  /**
   * Start GPS tracking
   */
  const startGpsTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setGpsStatus('acquiring');

    const id = navigator.geolocation.watchPosition(
      updateUserPosition,
      handleGpsError,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );

    setWatchId(id);
  }, [updateUserPosition, handleGpsError]);

  /**
   * Stop GPS tracking
   */
  const stopGpsTracking = useCallback(() => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setGpsStatus('disabled');
    setCanUseDynamic(false);
  }, [watchId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  // Auto-advance steps in dynamic mode based on position
  useEffect(() => {
    if (mode !== 'dynamic' || !route || !userPosition || isComplete) return;

    const currentStep = route.steps[currentStepIndex];
    if (!currentStep) return;

    // Check if user is close to the next waypoint
    const distanceToNext = calculateDistance(
      userPosition.lat,
      userPosition.lng,
      currentStep.endLat,
      currentStep.endLng
    );

    // If within 3 meters of next waypoint, advance step
    if (distanceToNext < 3) {
      nextStep();
    }
  }, [mode, route, userPosition, currentStepIndex, isComplete, nextStep]);

  const value = {
    // State
    mode,
    canUseDynamic,
    gpsStatus,
    route,
    isLoading,
    error,
    currentStepIndex,
    isComplete,
    userPosition,
    accessibleOnly,
    preferElevators,
    // Navigation actions
    startNavigation,
    startNavigationToCustom,
    startNavigationToNode,
    cancelNavigation,
    nextStep,
    previousStep,
    goToStep,
    // Mode controls
    enableDynamicMode,
    disableDynamicMode,
    // GPS controls
    startGpsTracking,
    stopGpsTracking,
    // Setters
    setError,
    setAccessibleOnly,
    setPreferElevators,
    // Computed values
    currentStep: route?.steps?.[currentStepIndex] || null,
    totalSteps: route?.steps?.length || 0,
    totalDistance: route?.route?.total_distance_meters || 0,
    totalTime: route?.route?.total_time_seconds || 0,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Calculate distance between two lat/lng points in meters (Haversine formula)
 */
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}
